import React, { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import { useOptimizedScroll } from '../hooks/useOptimizedScroll'
import { useIsMobile } from '../hooks/useMediaQuery'
import { PageMobileFooter } from './layout/PageMobileFooter'
import { faqPageFooter } from './layout/footerConfigs'
import { SEOHead } from './SEO/SEOHead'
import { getStructuredData } from '../config/structuredData'

export const FAQPage: React.FC = () => {
  const isMobile = useIsMobile()
  const [activeCategory, setActiveCategory] = useState('General')
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null)

  const prefersReducedMotion = useRef(
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  )

  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Use optimized scroll hook to prevent flickering
  const { scrollProgress } = useOptimizedScroll()

  return (
    <>
      {/* SEO Head */}
      <SEOHead
        customTitle="FAQ - Mernaki | Frequently Asked Questions"
        customDescription="Find answers to frequently asked questions about Mernaki services, pricing, integration, and support."
        customMetadata={{
          structuredData: getStructuredData('faq')
        }}
      />

      {/* Navbar is now global - no individual page navbars needed */}

      <motion.div
        className="min-h-screen relative"
        style={{
          backgroundColor: '#F7F5F5',
          borderBottomLeftRadius: '48px',
          borderBottomRightRadius: '48px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
          overflow: 'hidden',
          zIndex: 10,
          paddingBottom: '100px',
          transform: `translateY(${scrollProgress * -20}px)`
        }}
        transition={{ duration: 0 }}
      >
        {/* Randomly Moving Gradient Orbs */}
        <motion.div
          className="absolute w-96 h-96 rounded-full opacity-20 blur-3xl pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
            top: '12%',
            left: '10%',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={prefersReducedMotion.current ? {} : {
            x: [0, 90, -70, 0],
            y: [0, -70, 90, 0],
            scale: [1, 0.85, 1.25, 1]
          }}
          transition={{
            duration: 26,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        />
        <motion.div
          className="absolute w-80 h-80 rounded-full opacity-20 blur-3xl pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
            top: '45%',
            right: '10%',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={prefersReducedMotion.current ? {} : {
            x: [0, -100, 80, 0],
            y: [0, 80, -70, 0],
            scale: [1, 1.2, 0.9, 1]
          }}
          transition={{
            duration: 31,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        />
        <motion.div
          className="absolute w-72 h-72 rounded-full opacity-15 blur-3xl pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)',
            bottom: '18%',
            left: '18%',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={prefersReducedMotion.current ? {} : {
            x: [0, -90, 70, 0],
            y: [0, 60, -80, 0],
            scale: [1, 1.1, 0.95, 1]
          }}
          transition={{
            duration: 28,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        />

        <HeroSection prefersReducedMotion={prefersReducedMotion.current} />
        <CategorySelector activeCategory={activeCategory} setActiveCategory={setActiveCategory} prefersReducedMotion={prefersReducedMotion.current} />
        <FAQSection activeCategory={activeCategory} expandedFaq={expandedFaq} setExpandedFaq={setExpandedFaq} prefersReducedMotion={prefersReducedMotion.current} />
        <CTASection prefersReducedMotion={prefersReducedMotion.current} />
      </motion.div>

      <FooterSection isMobile={isMobile} />
    </>
  )
}

const HeroSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })

  return (
    <section ref={ref} className="relative pt-48 pb-32 overflow-hidden" style={{ backgroundColor: 'transparent' }}>
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.h1 className="text-5xl md:text-6xl lg:text-7xl mb-6 leading-tight" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '-0.03em' }} initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}>
          FAQ – Frequently Asked Questions
        </motion.h1>
        <motion.p className="text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 500 }} initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.3, ease: [0.4, 0.0, 0.2, 1] }}>
          Find quick answers about our services, software, hardware, and business solutions.
        </motion.p>
      </div>
    </section>
  )
}


const CategorySelector: React.FC<{ activeCategory: string; setActiveCategory: (cat: string) => void; prefersReducedMotion: boolean }> = ({ activeCategory, setActiveCategory, prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })

  const categories = ['General', 'Rare Earth', 'VerpackG & Compliance', 'Software', 'Hardware', 'Billing', 'Support']

  return (
    <section ref={ref} className="relative py-12" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="flex flex-wrap items-center justify-center gap-3"
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.4, 0.0, 0.2, 1] }}
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className="px-6 py-3 rounded-full text-sm transition-all duration-300"
              style={{
                backgroundColor: activeCategory === category ? '#1A1A1A' : '#FFFFFF',
                color: activeCategory === category ? '#FFFFFF' : '#666666',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600,
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                if (activeCategory !== category) {
                  e.currentTarget.style.backgroundColor = '#F9FAFB'
                }
              }}
              onMouseLeave={(e) => {
                if (activeCategory !== category) {
                  e.currentTarget.style.backgroundColor = '#FFFFFF'
                }
              }}
            >
              {category}
            </button>
          ))}
        </motion.div>
      </div>
    </section>
  )
}


const FAQSection: React.FC<{ activeCategory: string; expandedFaq: number | null; setExpandedFaq: (id: number | null) => void; prefersReducedMotion: boolean }> = ({ activeCategory, expandedFaq, setExpandedFaq, prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.1 })

  const faqData: Record<string, Array<{ q: string; a: string }>> = {
    'General': [
      { q: 'What is Mernaki?', a: 'Mernaki is a multidisciplinary technology company building intelligent services, next-generation hardware, and future-defining software experiences for businesses worldwide.' },
      { q: 'What services does Mernaki offer?', a: 'We offer rare earth sourcing services, compliance solutions (VerpackG/LUCID), recommerce platforms, software development tools, and hardware products.' },
      { q: 'How do I contact support?', a: 'You can reach our support team at support@mernaki.de or through our contact form. We typically respond within 24 hours during business days.' }
    ],
    'Rare Earth': [
      { q: 'How do your rare earth sourcing services work?', a: 'We connect verified buyers with certified suppliers, handle documentation, ensure compliance with international regulations, and facilitate secure transactions.' },
      { q: 'Do you provide verified buyers?', a: 'Yes, all buyers on our platform are thoroughly vetted and verified to ensure legitimate business operations and compliance with export/import regulations.' },
      { q: 'What purity levels do you support?', a: 'We support various purity levels depending on the specific rare earth element and application requirements. Contact our team for specific purity specifications.' },
      { q: 'How fast can you source materials?', a: 'Sourcing timelines vary based on material type, quantity, and purity requirements. Typical timelines range from 2-8 weeks for standard orders.' },
      { q: 'Are you certified to work with exporters/importers?', a: 'Yes, we maintain all necessary certifications and comply with international trade regulations including EU, US, and Asian export/import requirements.' }
    ],
    'VerpackG & Compliance': [
      { q: 'Do you offer full LUCID/VerpackG registration?', a: 'Yes, we provide complete LUCID and VerpackG registration services including documentation preparation, submission, and ongoing compliance management.' },
      { q: 'Do you handle deletion requests?', a: 'Yes, we manage VerpackG deletion requests (Löschung) for businesses that no longer require registration, ensuring proper documentation and compliance.' },
      { q: 'What industries do you support?', a: 'We support all industries required to register under German packaging law, including e-commerce, manufacturing, retail, and distribution companies.' },
      { q: 'How long does the process take?', a: 'Standard registration typically takes 5-10 business days. Deletion requests are usually processed within 3-5 business days.' }
    ],
    'Software': [
      { q: 'What platforms are supported?', a: 'Our software products support macOS, Windows, and Linux. Specific platform requirements vary by product - check individual product pages for details.' },
      { q: 'Is there an early access program?', a: 'Yes, we offer early access programs for select products. Join our mailing list or contact us to learn about upcoming releases and beta opportunities.' },
      { q: 'Do you support enterprise licensing?', a: 'Yes, we offer enterprise licensing with volume discounts, dedicated support, and custom deployment options. Contact our sales team for details.' },
      { q: 'Can I request custom features?', a: 'Enterprise customers can request custom features and integrations. Contact our team to discuss your specific requirements and feasibility.' }
    ],
    'Hardware': [
      { q: 'When will the device be available?', a: 'Our hardware products are currently in development. Sign up for updates to be notified when pre-orders and release dates are announced.' },
      { q: 'Will it run macOS apps or only games?', a: 'Our handheld device is designed primarily for gaming but will support select productivity applications. Full specifications will be announced closer to launch.' },
      { q: 'What is the expected battery life?', a: 'We are targeting 6-8 hours of active gaming and up to 12 hours for general use. Final specifications will be confirmed before launch.' },
      { q: 'Will it be compatible with existing platforms?', a: 'Yes, our hardware is designed to integrate seamlessly with our software ecosystem and support industry-standard protocols and formats.' }
    ],
    'Billing': [
      { q: 'What payment methods do you accept?', a: 'We accept credit cards (Visa, Mastercard, American Express), PayPal, bank transfers (SEPA), and invoice payments for enterprise customers.' },
      { q: 'Do you offer refunds?', a: 'Yes, we offer refunds within 14 days of purchase for most services. Hardware products have a 30-day return policy. See our refund policy for details.' },
      { q: 'Can I get a custom invoice?', a: 'Yes, enterprise customers can request custom invoices with specific billing details. Contact our billing team with your requirements.' },
      { q: 'How do subscriptions work?', a: 'Subscriptions are billed monthly or annually depending on your plan. You can upgrade, downgrade, or cancel at any time from your account dashboard.' }
    ],
    'Support': [
      { q: 'Do you offer business support packages?', a: 'Yes, we offer tiered support packages including priority response times, dedicated account managers, and 24/7 availability for enterprise customers.' },
      { q: 'How fast do you respond to tickets?', a: 'Standard support tickets receive responses within 24 hours. Priority support customers receive responses within 4 hours, and enterprise customers have dedicated support channels.' },
      { q: 'Where can I submit a request?', a: 'You can submit support requests through our Help Center, email support@mernaki.de, or use the contact form on our website.' }
    ]
  }

  const currentFaqs = faqData[activeCategory] || []

  return (
    <section ref={ref} className="relative py-24" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-4">
          {currentFaqs.map((faq, index) => (
            <motion.div
              key={index}
              className="rounded-2xl overflow-hidden"
              style={{
                backgroundColor: '#FFFFFF',
                border: '1px solid rgba(0, 0, 0, 0.1)'
              }}
              initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.05, ease: [0.4, 0.0, 0.2, 1] }}
            >
              <button
                className="w-full p-6 text-left flex items-center justify-between transition-colors duration-300"
                onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                style={{ backgroundColor: expandedFaq === index ? '#F9FAFB' : 'transparent' }}
              >
                <h3 className="text-lg pr-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
                  {faq.q}
                </h3>
                <svg
                  className="w-6 h-6 flex-shrink-0 transition-transform duration-300"
                  style={{ color: '#666666', transform: expandedFaq === index ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {expandedFaq === index && (
                <motion.div
                  className="px-6 pb-6"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <p className="text-base leading-relaxed" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }}>
                    {faq.a}
                  </p>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

const CTASection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })

  return (
    <section ref={ref} className="relative py-24" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          className="p-12 rounded-3xl"
          style={{
            backgroundColor: '#FFFFFF',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)'
          }}
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <h2 className="text-3xl md:text-4xl mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
            Still need help?
          </h2>
          <p className="text-lg mb-8" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }}>
            Our support team is available to assist you.
          </p>

          <Link
            to="/contact"
            className="inline-block px-8 py-4 rounded-xl text-base transition-all duration-300"
            style={{
              backgroundColor: '#1A1A1A',
              color: '#FFFFFF',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 600,
              textDecoration: 'none'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#333333'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1A1A1A'}
          >
            Contact Support
          </Link>
        </motion.div>
      </div>
    </section>
  )
}


const FooterSection: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })

  if (isMobile) {
    return <PageMobileFooter {...faqPageFooter} />
  }

  return (
    <footer ref={ref} className="relative" style={{ backgroundColor: '#0E0E0E', position: 'sticky', bottom: 0, zIndex: 1, paddingTop: '140px', paddingBottom: '40px', marginTop: '-80px' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-12">
          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
          >
            <div className="flex flex-col items-center gap-3 mb-6">
              <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.4)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }}>
                Mernaki © {new Date().getFullYear()}
              </p>
              <div className="flex items-center gap-3">
                <span className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.4)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }}>Service</span>
                <span style={{ color: 'rgba(255, 255, 255, 0.2)' }}>•</span>
                <span className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.4)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }}>Software</span>
                <span style={{ color: 'rgba(255, 255, 255, 0.2)' }}>•</span>
                <span className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.4)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }}>Hardware</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-4 text-sm mb-8">
              <Link to="/" style={{ color: 'rgba(255, 255, 255, 0.4)', textDecoration: 'none', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.4)'}>Home</Link>
              <span style={{ color: 'rgba(255, 255, 255, 0.2)' }}>|</span>
              <Link to="/about" style={{ color: 'rgba(255, 255, 255, 0.4)', textDecoration: 'none', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.4)'}>About</Link>
              <span style={{ color: 'rgba(255, 255, 255, 0.2)' }}>|</span>
              <Link to="/help" style={{ color: 'rgba(255, 255, 255, 0.4)', textDecoration: 'none', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.4)'}>Help Center</Link>
              <span style={{ color: 'rgba(255, 255, 255, 0.2)' }}>|</span>
              <Link to="/contact" style={{ color: 'rgba(255, 255, 255, 0.4)', textDecoration: 'none', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.4)'}>Contact</Link>
              <span style={{ color: 'rgba(255, 255, 255, 0.2)' }}>|</span>
              <Link to="/privacy-policy" style={{ color: 'rgba(255, 255, 255, 0.4)', textDecoration: 'none', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.4)'}>Privacy Policy</Link>
              <span style={{ color: 'rgba(255, 255, 255, 0.2)' }}>|</span>
              <Link to="/impressum" style={{ color: 'rgba(255, 255, 255, 0.4)', textDecoration: 'none', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.4)'}>Impressum</Link>
            </div>

            <div className="flex items-center justify-center gap-3">
              <img
                src="/Mernaki_Logo_Pack/transparent/mernaki-128.png"
                alt="Mernaki Logo"
                className="object-contain"
                style={{ height: '42px', width: 'auto', opacity: 0.85, filter: 'brightness(0) invert(1)' }}
                loading="lazy"
              />
              <h3
                className="text-lg"
                style={{
                  color: '#FFFFFF',
                  fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive",
                  fontWeight: 400,
                  opacity: 0.7
                }}
              >
                Mernaki
              </h3>
            </div>
          </motion.div>
        </div>

        <div className="border-t py-6" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
          <motion.div
            className="text-center"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
          >
            <p className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.3)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 300 }}>
              Engineered in Germany. Built for global scale.
            </p>
          </motion.div>
        </div>
      </div>
    </footer>
  )
}
